import { FC } from 'react';
import {
  FraudTrendOutcome,
  IAdvancedFraudTrendPattern,
  IFraudPatternEvidences
} from 'src/ts/interfaces/listCheck';
import { ChipTone } from '../../../helpers/listCheckHelpers';
import { FraudTrendEvidenceResult } from '../../../hooks/useFraudTrendEvidence';
import {
  ListCheckEmptyState,
  OverviewCard,
  OverviewCardBody,
  OverviewCardHead,
  OverviewCardList,
  OverviewResult
} from '../../../styles';
import ListCheckChip from '../ListCheckChip';
import { listCheckCopy } from '../listCheckCopy';
import { patternResultLines } from './advancedFraudTrendsModel';
import { CouldNotTellLine, OverviewIntro, RulesFound, TileRow } from './styles';

interface OverviewTabProps {
  patterns: IAdvancedFraudTrendPattern[];
  evidence: FraudTrendEvidenceResult;
  outcomeLabels?: Partial<Record<FraudTrendOutcome, string>>;
}

const copy = listCheckCopy.advancedFraudTrends;

const TONE: Record<FraudTrendOutcome, ChipTone> = {
  blocked: 'bad',
  consistent: 'neutral',
  inconclusive: 'open'
};

const outcomeText = (
  outcome: FraudTrendOutcome,
  labels: Partial<Record<FraudTrendOutcome, string>> | undefined
): string => (outcome === 'inconclusive' ? copy.couldNotTell : labels?.[outcome] ?? '');

export const PatternResults: FC<{
  pattern: IAdvancedFraudTrendPattern;
  evidence: FraudTrendEvidenceResult;
  tile?: boolean;
}> = ({ pattern, evidence, tile = false }) => {
  if (evidence.status === 'idle' || evidence.status === 'loading') {
    return <ListCheckEmptyState>{copy.loading}</ListCheckEmptyState>;
  }
  if (evidence.status !== 'success') {
    return <ListCheckEmptyState>{copy.unavailable}</ListCheckEmptyState>;
  }
  const lines = patternResultLines(
    pattern,
    evidence.data as unknown as IFraudPatternEvidences | null,
    tile
  );
  if (!lines.length) return <ListCheckEmptyState>{copy.noResults}</ListCheckEmptyState>;
  const rules = lines.filter((line) => !line.support);
  return (
    <>
      {tile && (
        <RulesFound data-testid="advanced-fraud-trends-rules-found">
          <strong>{rules.filter((line) => line.found).length}</strong>
          {copy.rulesFound(rules.length)}
        </RulesFound>
      )}
      {tile &&
        lines.map((line) => (
          <TileRow
            key={line.key}
            data-testid="advanced-fraud-trends-result-line"
            data-role={line.support ? 'support' : 'rule'}
            $support={line.support}
            $found={line.found}
          >
            <span>{line.label}</span>
            <b>{line.couldNotTell ? copy.couldNotTell : line.count ?? ''}</b>
          </TileRow>
        ))}
      {!tile &&
        lines.map((line) =>
          line.couldNotTell || line.support ? (
            <CouldNotTellLine
              key={line.key}
              data-testid="advanced-fraud-trends-result-line"
              data-role={line.support ? 'support' : 'rule'}
            >
              {line.text}
            </CouldNotTellLine>
          ) : (
            <OverviewResult
              key={line.key}
              data-testid="advanced-fraud-trends-result-line"
              data-role="rule"
            >
              {line.text}
            </OverviewResult>
          )
        )}
    </>
  );
};

const OverviewTab: FC<OverviewTabProps> = ({ patterns, evidence, outcomeLabels }) => (
  <>
    <OverviewIntro data-testid="advanced-fraud-trends-overview-intro">
      {copy.overviewIntro}
    </OverviewIntro>
    <OverviewCardList>
      {patterns.map((pattern) => {
        const hit = pattern.outcome === 'blocked';
        const text = pattern.outcome ? outcomeText(pattern.outcome, outcomeLabels) : '';
        return (
          <OverviewCard
            key={pattern.key}
            $hit={hit}
            data-testid="advanced-fraud-trends-tile"
            data-pattern={pattern.key}
          >
            <OverviewCardHead $hit={hit}>
              {pattern.name}
              {patterns.length > 1 && pattern.outcome && text && (
                <ListCheckChip chip={{ tone: TONE[pattern.outcome], text }} />
              )}
            </OverviewCardHead>
            <OverviewCardBody>
              <PatternResults pattern={pattern} evidence={evidence} tile />
            </OverviewCardBody>
          </OverviewCard>
        );
      })}
    </OverviewCardList>
  </>
);

export default OverviewTab;
